/** @jsxImportSource ./simboluri-jsx */
// DESENUL MIC — un `DrawingData` (același JSON pe care îl randează
// `DrawingView` pe pânză) ca SVG inline, încadrat într-o cutie: polilinii și
// texte, atât. Există pentru locurile în care desenul e o ILUSTRAȚIE, nu o
// unealtă — forma unei mărci în cartonașul de hover, o miniatură într-un
// tabel —, unde pânza vizorului, cu straturile și mouse-ul ei, ar fi prea
// mult. Fără pragma de simboluri: n-are text de adnotat, doar cifre de cotă.

import type { DrawingData, DrawingEntity } from "./tipuri";

type Cutie = { minx: number; miny: number; maxx: number; maxy: number };

/** Dreptunghiul care cuprinde entitățile desenate (cel din `bounds` când e
 *  dat și nu e degenerat). */
export function cutiaDesenului(d: DrawingData): Cutie | null {
  const b = d.bounds;
  if (b && b.maxx > b.minx && b.maxy > b.miny) return b;
  let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
  for (const e of d.entities) {
    const pts: [number, number][] = e.t === "p" ? e.pts : [[e.x, e.y]];
    for (const [x, y] of pts) {
      if (x < minx) minx = x;
      if (y < miny) miny = y;
      if (x > maxx) maxx = x;
      if (y > maxy) maxy = y;
    }
  }
  if (!Number.isFinite(minx) || maxx <= minx || maxy <= miny) return null;
  return { minx, miny, maxx, maxy };
}

/** Culoarea unei entități: a ei, altfel a stratului. */
function culoare(d: DrawingData, e: DrawingEntity): string {
  return e.c || d.layers[e.l]?.color || "currentColor";
}

// punctul de prindere MTEXT 1..9: 1=stânga-sus … 9=dreapta-jos
function ancoraText(a: number): {
  anchor: "start" | "middle" | "end";
  baseline: "hanging" | "middle" | "alphabetic";
} {
  const col = ((a - 1) % 3);
  const rand = Math.floor((a - 1) / 3);
  return {
    anchor: col === 0 ? "start" : col === 1 ? "middle" : "end",
    baseline: rand === 0 ? "hanging" : rand === 1 ? "middle" : "alphabetic",
  };
}

export default function MiniDesen({ desen, latime = 300, inaltime = 160, margine = 8 }: {
  desen: DrawingData;
  latime?: number;
  inaltime?: number;
  /** spațiul lăsat în jurul desenului, în px */
  margine?: number;
}) {
  const c = cutiaDesenului(desen);
  if (!c) return null;
  const lw = c.maxx - c.minx, lh = c.maxy - c.miny;
  const scara = Math.min((latime - 2 * margine) / lw, (inaltime - 2 * margine) / lh);
  // desenul se centrează în cutie; y-ul DXF crește în sus, al SVG-ului în jos
  const dx = (latime - lw * scara) / 2, dy = (inaltime - lh * scara) / 2;
  const X = (x: number) => dx + (x - c.minx) * scara;
  const Y = (y: number) => inaltime - dy - (y - c.miny) * scara;
  const grosime = Math.max(0.6, Math.min(1.6, 0.02 * scara));
  return (
    <svg width={latime} height={inaltime} viewBox={`0 0 ${latime} ${inaltime}`}
         role="img" data-testid="mini-desen"
         style={{ display: "block", maxWidth: "100%" }}>
      {desen.entities.map((e, i) => {
        if (e.t === "p") {
          if (e.pts.length < 2) return null;
          const p = e.pts.map(([x, y]) => `${X(x).toFixed(1)},${Y(y).toFixed(1)}`).join(" ");
          const col = culoare(desen, e);
          return e.closed
            ? <polygon key={i} points={p} fill={e.fill ?? "none"} stroke={col}
                       strokeWidth={e.w ? Math.max(grosime, e.w / 40) : grosime} />
            : <polyline key={i} points={p} fill="none" stroke={col}
                        strokeWidth={e.w ? Math.max(grosime, e.w / 40) : grosime}
                        strokeLinecap="round" strokeLinejoin="round" />;
        }
        if (e.t === "t") {
          const fs = Math.max(6, e.h * scara);
          const { anchor, baseline } = ancoraText(e.a || 7);
          const x = X(e.x), y = Y(e.y);
          return (
            <text key={i} x={x} y={y} fontSize={fs} fill={culoare(desen, e)}
                  textAnchor={anchor} dominantBaseline={baseline}
                  fontFamily="ui-monospace, monospace"
                  transform={e.r ? `rotate(${-e.r} ${x} ${y})` : undefined}>
              {e.s}
            </text>
          );
        }
        return <circle key={i} cx={X(e.x)} cy={Y(e.y)} r={1.5} fill={culoare(desen, e)} />;
      })}
    </svg>
  );
}
